import { useEffect, useState } from "react";
import { Dropzone, FileRejection, MIME_TYPES } from "@mantine/dropzone";
import { BackgroundImage } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpFromBracket,
  faEye,
  faFilePdf,
  faPlus,
  faTrash,
} from "@fortawesome/sharp-regular-svg-icons";
import classNames from "classnames";
import { kebabCase } from "lodash";
import { FileError } from "react-dropzone";
import styles from "./upload-file-input.module.scss";
import { getDisplayValueForFileSize, MEGABYTE } from "@/utils/fileSize.ts";
import { MeatballMenu } from "@/components/Menus/MeatballMenu";

const TWENTY_MEGABYTES = 20 * MEGABYTE;

type UploadFileInputProps = {
  className?: string;
  onViewFile: (File: File) => void;
  files?: File[];
  onFilesUploaded?: (files: File[]) => void;
  onFileDeleted?: (file: File) => void;
  onFileRejected?: (fileRejections: FileRejection[]) => void;
  disabled?: boolean;
};

const UploadFileInput = ({
  className,
  onViewFile,
  files: controlledFiles = [],
  onFilesUploaded,
  onFileDeleted,
  onFileRejected,
  disabled,
}: UploadFileInputProps) => {
  const [files, setFiles] = useState<File[]>(controlledFiles);

  useEffect(() => {
    setFiles(controlledFiles);
  }, [controlledFiles]);

  const validateUploadedFiles = (uploadedFile: File): FileError[] | null => {
    const hasDuplicateName = files.some(
      (file) => file.name === uploadedFile.name,
    );

    if (hasDuplicateName) {
      return [
        {
          code: "file-duplicate-name",
          message: `A file named "${uploadedFile.name}" already exists. Please rename the file and try again.`,
        },
      ];
    }

    return null;
  };

  const handleOnDrop = (uploadedFiles: File[]) => {
    if (onFilesUploaded) {
      onFilesUploaded(uploadedFiles);
    } else {
      setFiles([...files, ...uploadedFiles]);
    }
  };

  const handleOnFileDelete = (fileToDelete: File) => {
    if (onFileDeleted) {
      onFileDeleted(fileToDelete);
    } else {
      setFiles(files.filter((file) => file !== fileToDelete));
    }
  };

  if (!files.length) {
    return (
      <Dropzone
        classNames={{
          root: classNames(styles.uploadFirstFileZone, className),
          inner: styles.uploadFileZoneContent,
        }}
        accept={[MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.jpeg]}
        multiple
        inputProps={{
          "aria-label": "Upload files",
        }}
        onDrop={handleOnDrop}
        onReject={onFileRejected}
        disabled={disabled}
        validator={validateUploadedFiles}
        maxSize={TWENTY_MEGABYTES}
      >
        <div className={styles.uploadIconContainer}>
          <FontAwesomeIcon icon={faArrowUpFromBracket} />
        </div>
        <div className={styles.fileZoneDescriptionContainer}>
          <span className={styles.dropZoneHeading}>
            {"Tap here to add a file or photo"}
          </span>
          <span className={styles.dropZoneDescriptionLine}>
            {"Ensure all pages of your document are included."}
          </span>
          <span className={styles.dropZoneDescriptionLine}>
            {"Maximum file size 20MB"}
          </span>
        </div>
      </Dropzone>
    );
  }

  return (
    <div
      className={classNames(styles.uploadAdditionalFilesContainer, className)}
    >
      {files.map((file, fileIndex) => (
        <div key={file.name} className={styles.uploadedFileContainer}>
          <div className={styles.uploadedFilePreviewContainer}>
            {([MIME_TYPES.png, MIME_TYPES.jpeg] as string[]).includes(
              file.type,
            ) && (
              <BackgroundImage
                src={URL.createObjectURL(file)}
                className={styles.uploadedImageThumbnail}
              />
            )}
            {file.type === MIME_TYPES.pdf && (
              <FontAwesomeIcon icon={faFilePdf} size="2x" />
            )}
          </div>
          <div className={styles.uploadedFileDetails}>
            <span
              className={styles.uploadedFilePageNumber}
            >{`Upload ${fileIndex + 1}`}</span>
            <span
              className={styles.uploadedFileInfo}
            >{`${file.name} - ${getDisplayValueForFileSize(file.size)}`}</span>
          </div>
          <MeatballMenu
            keyPrefix={kebabCase(file.name)}
            buttonAriaLabel="Options for file upload"
            className={styles.uploadedFileOptionsButton}
            actions={[
              {
                label: "View",
                icon: faEye,
                onClick: () => onViewFile(file),
              },
              {
                label: "Delete",
                icon: faTrash,
                onClick: () => handleOnFileDelete(file),
              },
            ]}
            disabled={disabled}
          />
        </div>
      ))}
      <Dropzone
        onDrop={handleOnDrop}
        classNames={{
          root: styles.uploadAdditionalFileZone,
          inner: styles.uploadFileZoneContent,
        }}
        accept={[MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.jpeg]}
        multiple
        inputProps={{
          "aria-label": "Upload files",
        }}
        onReject={onFileRejected}
        disabled={disabled}
        validator={validateUploadedFiles}
        maxSize={TWENTY_MEGABYTES}
      >
        <FontAwesomeIcon icon={faPlus} />
        <span className={styles.addAdditionalFilesDescription}>
          {"ADD FILE"}
        </span>
      </Dropzone>
    </div>
  );
};

export default UploadFileInput;
